'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Users } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTrialAccessStatus } from '@/hooks/useTrialAccess';
import {
  TRIAL_ALLOWED_ROUTES,
  TRIAL_DIALOG_FREE_ROUTES,
  matchesTrialRoute,
} from '@/lib/trialAccessRoutes';
import { useUserLogged } from '@/hooks/useUserLogged';

/**
 * Locks the dashboard for someone whose workspace has run out of trial seats.
 *
 * Two halves, both keyed off the same `locked` predicate as the sidebar filter:
 * this component bounces disallowed routes back to `/home` and explains why,
 * and the sidebar hides every nav item except Home. Hiding nav alone would not
 * stop anyone typing a URL, which is what the redirect is for.
 *
 * This is a product gate, not a security boundary — the person is still a
 * legitimate member of the org, and PR reviews (the metered resource) are
 * refused server-side regardless of what the browser does.
 */
export default function TrialSeatGate() {
  const pathname = usePathname() || '/';
  const router = useRouter();
  const { user, organization } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const { trialAccess, locked } = useTrialAccessStatus(orgUuid);
  const [dismissed, setDismissed] = useState(false);

  const onAllowedRoute = matchesTrialRoute(pathname, TRIAL_ALLOWED_ROUTES);

  useEffect(() => {
    if (locked && !onAllowedRoute) {
      router.replace('/home');
    }
  }, [locked, onAllowedRoute, router]);

  // Dismissible on purpose. The nav is already reduced to Home, so the state
  // stays legible after the dialog closes — and a permanent backdrop would make
  // Home as unusable as the pages we redirected away from, leaving no route
  // that actually works.
  const showDialog =
    locked && !dismissed && !matchesTrialRoute(pathname, TRIAL_DIALOG_FREE_ROUTES);

  if (!showDialog) return null;

  const seatCap = trialAccess.SeatCap ?? 0;

  return (
    <Dialog open onOpenChange={(nextOpen) => !nextOpen && setDismissed(true)}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <span className="flex size-9 items-center justify-center rounded-md bg-ds-brand-subtlest">
            <Users size={20} weight="regular" className="text-ds-brand-subtle" />
          </span>
          <DialogTitle className="ds-text-title-sm-semibold pt-3 text-ds-text-darker">
            Your workspace&apos;s trial is full
          </DialogTitle>
          <DialogDescription className="ds-text-body-sm-medium pt-1 text-ds-text-subtle">
            {seatCap > 0 ? (
              <>
                This trial covers{' '}
                <span className="ds-text-code-md text-ds-text-darker">
                  {seatCap}
                </span>{' '}
                {seatCap === 1 ? 'person' : 'people'}, and every seat is in use
                right now. Ask an admin in your workspace for access.
              </>
            ) : (
              <>
                Every seat in this trial is in use right now. Ask an admin in
                your workspace for access.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={() => setDismissed(true)}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
